/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { MigrationModalFooter } from 'src/verticals/azureMigrationModal/newComponents/MigrationModalFooter';

export type RegionMigrationScreenProps = {
    /**
     * User agreed to continue in the migration wizard.
     */
    onContinue: () => void;

    /**
     * User wishes to go back the migration modal.
     */
    onPrevious: () => void;
};

const messages = defineMessages({
    title: {
        id: 'multiRegionMigrationModal.regionMigrationScreen.title',
        defaultMessage: 'Regional migration'
    },
    bodyPart1: {
        id: 'multiRegionMigrationModal.regionMigrationScreen.bodyPart1',
        defaultMessage: `Applications must be migrated to an Azure resource in the same region it was created in.
You have applications in the following regions:`
    }
});

const { styleUlist } = getStylist('RegionMigrationScreen');
const RegionList = styleUlist('RegionList', () => ({ marginLeft: 20 }));

/**
 * Renders the RegionMigration screen of the migration flow.
 */
export const RegionMigrationScreen = observer((props: RegionMigrationScreenProps) => {
    const { onContinue, onPrevious } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const regions = rootStore.userMigrationInfoStoreHouse.items.map(store => store.region);

    return (
        <Stack verticalFill tokens={{ childrenGap: 20 }}>
            <Stack verticalFill tokens={{ childrenGap: 20 }}>
                <LuisText variant="xLarge" fontWeight={600}>
                    {localize(messages.title)}
                </LuisText>

                <LuisText lineHeight="1.5">{localize(messages.bodyPart1)}</LuisText>

                <RegionList>
                    {regions.map(region => (
                        <li key={region}>{region}</li>
                    ))}
                </RegionList>
            </Stack>

            <MigrationModalFooter backDisabled={false} nextDisabled={false} onContinue={onContinue} onPrevious={onPrevious} />
        </Stack>
    );
});
